"use client";

import Image from "next/image";
import Link from "next/link";
import { TeamMember } from "@/constants/about-us";
import { motion } from "motion/react";
import { ArrowUpLeft } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "./ui/carousel";
import { StaticImageData } from "next/image";
import { Title } from "./ui/title";
import { EASE } from "@/lib/motion";

type Props = {
  member: TeamMember & { images?: StaticImageData[] };
};

function splitLastWord(text: string): [string, string] {
  const trimmed = text.trim();
  const lastSpace = trimmed.lastIndexOf(" ");
  if (lastSpace === -1) return ["", trimmed];
  return [trimmed.slice(0, lastSpace), trimmed.slice(lastSpace + 1)];
}

export function MemberInfo({ member }: Props) {
  const images = member.images || [member.image];
  const [firstName, lastName] = splitLastWord(member.name);
  const initials =
    `${firstName?.[0] ?? ""}${lastName?.[0] ?? ""}`.toUpperCase();

  return (
    <section className="px-4 md:px-8 lg:px-12 pt-24">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, ease: EASE }}
        className="flex items-center justify-between border-b border-foreground/10 pb-2"
      >
        <Link
          href="/o-nama"
          className="group inline-flex items-center gap-2 text-foreground/55 hover:text-brand-800 transition-colors"
        >
          <ArrowUpLeft
            className="size-3.5 -mt-px transition-transform group-hover:-translate-x-0.5 group-hover:-translate-y-0.5"
            strokeWidth={1.5}
          />
          <span className="uppercase tracking-[0.22em] text-[10px]">
            Nazad / O nama
          </span>
        </Link>

        <span className="font-serif italic text-foreground/55 text-[13px] leading-none tabular-nums tracking-[-0.005em]">
          №{" "}
          <span className="not-italic font-sans tracking-[0.08em] text-foreground/70">
            {initials}
          </span>
        </span>
      </motion.div>

      <div className="grid grid-cols-12 gap-x-6 lg:gap-x-10 mt-10">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={{
            hidden: {},
            visible: {
              transition: { staggerChildren: 0.08, delayChildren: 0.05 },
            },
          }}
          className="col-span-12 lg:col-span-7 flex flex-col"
        >
          <motion.span
            variants={{
              hidden: { opacity: 0, y: 12 },
              visible: {
                opacity: 1,
                y: 0,
                transition: { duration: 0.7, ease: EASE },
              },
            }}
            className="font-serif italic text-brand-800 text-[15px] tracking-[-0.005em]"
          >
            Vol. I &mdash; Profil
          </motion.span>

          <motion.div
            variants={{
              hidden: { opacity: 0, y: 22 },
              visible: {
                opacity: 1,
                y: 0,
                transition: { duration: 0.95, ease: EASE },
              },
            }}
            className="mt-5 lg:mt-7"
          >
            <Title variant="hero" accent={lastName}>
              {firstName}
            </Title>
          </motion.div>

          <motion.p
            variants={{
              hidden: { opacity: 0, y: 14 },
              visible: {
                opacity: 1,
                y: 0,
                transition: { duration: 0.8, ease: EASE },
              },
            }}
            className="mt-6 text-foreground/65 text-[15px] leading-[1.6] tracking-[-0.005em] max-w-[44ch]"
          >
            {member.position}
            {member.years_of_experience
              ? `, s ${member.years_of_experience} godina kliničke prakse.`
              : "."}
          </motion.p>

          <motion.dl
            variants={{
              hidden: { opacity: 0, y: 14 },
              visible: {
                opacity: 1,
                y: 0,
                transition: { duration: 0.8, ease: EASE, delay: 0.1 },
              },
            }}
            className="mt-10 lg:mt-14 grid grid-cols-2 gap-x-8 gap-y-6 max-w-[520px] border-t border-foreground/10 pt-7"
          >
            {member.email && (
              <div className="flex flex-col gap-1.5">
                <dt className="uppercase tracking-[0.22em] text-[9.5px] text-foreground/45">
                  Kontakt
                </dt>
                <dd>
                  <a
                    href={`mailto:${member.email}`}
                    className="text-foreground/90 hover:text-brand-800 transition-colors text-[13.5px] tracking-[-0.005em]"
                  >
                    {member.email}
                  </a>
                </dd>
              </div>
            )}

            {member.linked_in && (
              <div className="flex flex-col gap-1.5">
                <dt className="uppercase tracking-[0.22em] text-[9.5px] text-foreground/45">
                  LinkedIn
                </dt>
                <dd>
                  <a
                    href={member.linked_in}
                    target="_blank"
                    rel="noreferrer"
                    className="group inline-flex items-baseline gap-1 text-foreground/90 hover:text-brand-800 transition-colors text-[13.5px] tracking-[-0.005em]"
                  >
                    Otvori profil
                    <span
                      aria-hidden
                      className="font-serif italic transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    >
                      ↗
                    </span>
                  </a>
                </dd>
              </div>
            )}

            {member.working_hours && (
              <div className="flex flex-col gap-1.5">
                <dt className="uppercase tracking-[0.22em] text-[9.5px] text-foreground/45">
                  Termini
                </dt>
                <dd className="text-foreground/90 text-[13.5px] tracking-[-0.005em] tabular-nums">
                  {member.working_hours}
                </dd>
              </div>
            )}

            {member.years_of_experience && (
              <div className="flex flex-col gap-1.5">
                <dt className="uppercase tracking-[0.22em] text-[9.5px] text-foreground/45">
                  Iskustvo
                </dt>
                <dd className="text-foreground/90 text-[13.5px] tracking-[-0.005em]">
                  <span className="font-serif italic text-foreground tabular-nums text-[18px] mr-1 leading-none">
                    {member.years_of_experience}
                  </span>
                  godina
                </dd>
              </div>
            )}
          </motion.dl>
        </motion.div>

        <motion.figure
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: EASE, delay: 0.15 }}
          className="col-span-12 lg:col-span-5 lg:col-start-8 row-start-2 lg:row-start-1 mt-10 lg:mt-0"
        >
          <div className="relative w-full aspect-[4/5] lg:aspect-square overflow-hidden bg-brand-100">
            <Carousel
              opts={{ loop: true, active: images.length > 1 }}
              className="w-full h-full overflow-hidden"
            >
              <CarouselContent className="h-full -ml-0">
                {images.map((image, index) => (
                  <CarouselItem
                    key={index}
                    className="basis-full shrink-0 pl-0 overflow-hidden"
                  >
                    <div className="relative w-full overflow-hidden aspect-[4/5] lg:aspect-square">
                      <Image
                        alt={`${member.name} portret ${index + 1}`}
                        src={image}
                        fill
                        className="object-cover object-[50%_28%]"
                        sizes="(max-width: 1024px) 100vw, 42vw"
                        placeholder="blur"
                        priority={index === 0}
                      />
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              {images.length > 1 && (
                <>
                  <CarouselPrevious className="absolute top-1/2 -translate-y-1/2 left-3 rounded-none shadow-none border border-background/40 bg-background/0 hover:bg-background/15 backdrop-blur size-9 cursor-pointer text-background hover:text-background" />
                  <CarouselNext className="absolute top-1/2 -translate-y-1/2 right-3 rounded-none shadow-none border border-background/40 bg-background/0 hover:bg-background/15 backdrop-blur size-9 cursor-pointer text-background hover:text-background" />
                </>
              )}
            </Carousel>
          </div>
        </motion.figure>
      </div>
    </section>
  );
}
